import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { FREE_TOOLS_LINKS } from '../data/freeTools';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';
import TermsOfServiceModal from '@/components/TermsOfServiceModal';
import CookiesPolicyModal from '@/components/CookiesPolicyModal';

interface IProps {
  scrollToSection?: (id: string) => void;
}

const Footer = (props: IProps) => {
  const navigate = useNavigate();

  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showCookiesModal, setShowCookiesModal] = useState(false);
  return (
    <div>
      <footer className="relative z-10 bg-gray-900 text-gray-300 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center mb-4">
                <img
                  src="/focuzonWhite.png"
                  alt="Focuzon Logo"
                  className="h-8 w-auto object-contain"
                  loading="lazy"
                  style={{ maxWidth: '140px', minWidth: '100px' }}
                />
              </div>
              <p className="text-sm text-gray-400">
                A workspace designed for deep, focused work: for individuals and teams.
              </p>
            </div>

            {/* Explore - section anchors on landing page */}
            <div>
              <h3 className="text-white font-semibold mb-4">Explore</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/#what')} className="hover:text-orange-400 transition-colors text-left">Features</button></li>
                <li><button onClick={() => navigate('/#how')} className="hover:text-orange-400 transition-colors text-left">How It Works</button></li>
                <li><button onClick={() => navigate('/#why')} className="hover:text-orange-400 transition-colors text-left">Why Focuzon</button></li>
                <li><button onClick={() => navigate('/#faq')} className="hover:text-orange-400 transition-colors text-left">FAQ</button></li>
              </ul>
            </div>

            {/* Techniques */}
            <div>
              <h3 className="text-white font-semibold mb-4">Techniques</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => { navigate('/pomodoro-timer'); window.scrollTo(0, 0); }} className="hover:text-orange-400 transition-colors text-left">Pomodoro Timer</button></li>
                <li><button onClick={() => { navigate('/deep-work'); window.scrollTo(0, 0); }} className="hover:text-orange-400 transition-colors text-left">Deep Work</button></li>
                <li><button onClick={() => { navigate('/focus-sounds'); window.scrollTo(0, 0); }} className="hover:text-orange-400 transition-colors text-left">Focus Sounds</button></li>
                <li><button onClick={() => { navigate('/task-manager'); window.scrollTo(0, 0); }} className="hover:text-orange-400 transition-colors text-left">Task Manager</button></li>
              </ul>
            </div>

            {/* Free Tools */}
            <div>
              <h3 className="text-white font-semibold mb-4">Free Tools</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/tools')} className="hover:text-orange-400 transition-colors text-left">All Tools</button></li>
                {FREE_TOOLS_LINKS.slice(0, 2).map((tool) => (
                  <li key={tool.path}>
                    <button onClick={() => navigate(tool.path)} className="hover:text-orange-400 transition-colors text-left">{tool.label}</button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/about')} className="hover:text-orange-400 transition-colors text-left">About</button></li>
                <li><button onClick={() => navigate('/blog')} className="hover:text-orange-400 transition-colors text-left">Blog</button></li>
                <li><a href="/#contact" className="hover:text-orange-400 transition-colors text-left">Contact</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <button
                    onClick={() => setShowPrivacyModal(true)}
                    className="hover:text-orange-400 transition-colors text-left"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setShowTermsModal(true)}
                    className="hover:text-orange-400 transition-colors text-left"
                  >
                    Terms of Service
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setShowCookiesModal(true)}
                    className="hover:text-orange-400 transition-colors text-left"
                  >
                    Cookie Policy
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/gdpr')}
                    className="hover:text-orange-400 transition-colors text-left"
                  >
                    GDPR
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-end items-center">
            <p className="text-sm text-gray-400 mb-4 md:mb-0">
              {`© ${new Date().getFullYear()} Focuzon. All rights reserved.`}
            </p>
            <div className="flex items-center space-x-6">
              <a
                href="https://www.linkedin.com/company/focuzon/"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 text-gray-400 hover:text-orange-400 transition-colors"
              >
                <span className="sr-only">LinkedIn</span>
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
      <TermsOfServiceModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
      <CookiesPolicyModal
        isOpen={showCookiesModal}
        onClose={() => setShowCookiesModal(false)}
      />
    </div>
  );
};

export default Footer;
